import { useEffect, useState } from "react";
import { IProject } from "../../../../interfaces/project.interface";
import "./Categories.css";
import {
    deleteCategory,
    deletePeriod,
    deleteProject,
    updateCategory,
} from "../../../../services/firestore.service";
import { ICategory } from "../../../../interfaces/category.interface";
import { IPeriod } from "../../../../interfaces/period.interface";
import { Timestamp } from "firebase/firestore";
import AddCategoryDialog from "../../../../components/AddCategoryDialog";
import { AddPeriodDialog } from "../../../../components/AddPeriodDialog";
import ConfirmDialog from "../../../../components/ConfirmDialog";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import { create } from "../../../../slices/dialogsSlice";
import { EditPeriodDialog } from "../../../../components/EditPeriodDialog";
import { DownloadCsvDialog } from "../../../../components/DownloadCsvDialog";
import EditCategoryDialog from "../../../../components/EditCategoryDialog";

const ChargableIcon = () => {
    return (
        <>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
                <path
                    d="M112 160.4c0-35.5 28.8-64.4 64.4-64.4c6.9 0 13.8 1.1 20.4 3.3l81.2 27.1c16.8 5.6 34.9-3.5 40.5-20.2s-3.5-34.9-20.2-40.5L217 38.6c-13.1-4.4-26.8-6.6-40.6-6.6C105.5 32 48 89.5 48 160.4L48 224l-16 0c-17.7 0-32 14.3-32 32s14.3 32 32 32l16 0 0 44.5c0 17.4-4.7 34.5-13.7 49.4L4.6 431.5c-5.9 9.9-6.1 22.2-.4 32.2S20.5 480 32 480l256 0c17.7 0 32-14.3 32-32s-14.3-32-32-32L88.5 416l.7-1.1C104.1 390 112 361.5 112 332.5l0-44.5 112 0c17.7 0 32-14.3 32-32s-14.3-32-32-32l-112 0 0-63.6z"
                    fill="currentColor"
                />
            </svg>
        </>
    );
};

const formatDateRange: (timestamp1: Timestamp, timestamp2: Timestamp) => string = (
    timestamp1: Timestamp,
    timestamp2: Timestamp,
) => {
    let date1 = timestamp1.toDate();
    let date2 = timestamp2?.toDate();

    if (!date2) {
        let monthString = (date1.getMonth() < 9 ? "0" : "") + (date1.getMonth() + 1);
        let dayString = (date1.getDate() < 9 ? "0" : "") + date1.getDate();

        let dateString = `${dayString}/${monthString}/${date1.getFullYear()}`;

        let hourString: string = (date1.getHours() < 10 ? "0" : "") + date1.getHours();
        let minsString: string = (date1.getMinutes() < 10 ? "0" : "") + date1.getMinutes();

        let timeString = `${hourString}:${minsString}`;

        return `${timeString}, ${dateString} - Unfinished`;
    }

    let timeRange = `${date1.getHours()}:${
        (date1.getMinutes() < 10 ? "0" : "") + date1.getMinutes()
    } - ${date2.getHours()}:${(date2.getMinutes() < 10 ? "0" : "") + date2.getMinutes()}`;

    let startRange = "";
    if (date1.getDate() !== date2.getDate()) {
        startRange += " " + date1.getDate();
    }
    if (date1.getMonth() !== date2.getMonth()) {
        startRange += "/" + (date1.getMonth() + 1);
    }
    if (date1.getFullYear() !== date2.getFullYear()) {
        startRange += "/" + date1.getFullYear();
    }

    let endRange = ` ${date2.getDate()}/${date2.getMonth() + 1}/${date2.getFullYear()}`;

    let dateRange = startRange + (!!startRange ? " - " : "") + endRange;

    return timeRange + ", " + dateRange;
};

interface PeriodEntryProps {
    project: IProject;
    category: ICategory;
    period: IPeriod;
    updateCategories: () => void;
}

const PeriodEntry = ({ project, category, period, updateCategories }: PeriodEntryProps) => {
    const dispatch = useAppDispatch();
    const isUnfinished = !period.endDateTime;

    const openEditPeriodDialog = () => {
        dispatch(
            create({
                id: 0,
                component: EditPeriodDialog,
                props: {
                    updatePeriods: updateCategories,
                    projectId: project.id,
                    categoryId: category.id,
                    period: period,
                },
            }),
        );
    };

    const doPeriodDelete = (e: any) => {
        e.stopPropagation();

        dispatch(
            create({
                id: 0,
                component: ConfirmDialog,
                props: {
                    resultListener: (result: boolean) => {
                        if (result)
                            deletePeriod(project.id, category.id, period.id).then(() => {
                                updateCategories();
                            });
                    },
                    warningText: "This action can not be undone.",
                },
            }),
        );
    };

    return (
        <div
            className={`period ${isUnfinished ? "unfinished" : ""}`}
            onClick={openEditPeriodDialog}
        >
            <div className="title">{period.title}</div>
            <div></div>
            <div className="person">{period.assignedTo}</div>
            <div className="date-range">
                {formatDateRange(period.startDateTime, period.endDateTime)}
            </div>
            {period.chargable ? <ChargableIcon></ChargableIcon> : <div></div>}
            <button className="warn" onClick={doPeriodDelete}>
                Delete
            </button>
        </div>
    );
};

interface CategoryColumnProps {
    project: IProject;
    category: ICategory;
    updateCategories: () => void;
    move: (direction: "FIRST" | "LEFT" | "RIGHT" | "LAST") => void;
}

const CategoryColumn = ({ project, category, updateCategories, move }: CategoryColumnProps) => {
    const dispatch = useAppDispatch();

    const openAddPeriodDialog = () => {
        dispatch(
            create({
                id: 0,
                component: AddPeriodDialog,
                props: {
                    updatePeriods: updateCategories,
                    projectId: project.id,
                    categoryId: category.id,
                },
            }),
        );
    };

    const openEditCategoryDialog = () => {
        dispatch(
            create({
                id: 0,
                component: EditCategoryDialog,
                props: {
                    updateCategories: updateCategories,
                    category: category,
                    projectId: project.id,
                },
            }),
        );
    };

    const doCategoryDelete = () => {
        dispatch(
            create({
                id: 0,
                component: ConfirmDialog,
                props: {
                    resultListener: (result: boolean) => {
                        if (result)
                            deleteCategory(project.id, category.id).then(() => {
                                updateCategories();
                            });
                    },
                    warningText: "This action can not be undone.",
                },
            }),
        );
    };

    const doCategoryHide = () => {
        category.hidden = true;

        updateCategory(project.id, category).then(() => {
            updateCategories();
        });
    };

    const doCategoryUnhide = () => {
        category.hidden = false;

        updateCategory(project.id, category).then(() => {
            updateCategories();
        });
    };

    const getTotalHours = () => {
        let hours = 0;

        for (let period of category.periods) {
            if (!period.endDateTime || !period.startDateTime) continue;

            let pHours = period.endDateTime.seconds - period.startDateTime.seconds;
            pHours = Math.round((pHours * 100) / 60 / 60) / 100;

            hours += pHours;
        }

        return hours;
    };

    return (
        <div className="category-column">
            <div className="top-row">
                <div className="button-row">
                    <button onClick={() => move("FIRST")}>First</button>
                    <button onClick={() => move("LEFT")}>Left</button>
                    <button onClick={() => move("RIGHT")}>Right</button>
                    <button onClick={() => move("LAST")}>Last</button>

                    <div className="sep"></div>

                    {category.hidden ? (
                        <button onClick={doCategoryUnhide}>Unhide</button>
                    ) : (
                        <button onClick={doCategoryHide}>Hide</button>
                    )}

                    <button onClick={openEditCategoryDialog}>Edit</button>

                    <button className="warn" onClick={doCategoryDelete}>
                        Delete
                    </button>
                </div>

                <h2>{category.title}</h2>
            </div>

            <div className="total-hours">{getTotalHours()} Hours</div>

            <div className="periods">
                {category.periods.map((p, i) => (
                    <PeriodEntry
                        key={i}
                        project={project}
                        category={category}
                        period={p}
                        updateCategories={updateCategories}
                    />
                ))}
            </div>

            <button onClick={openAddPeriodDialog}>New Period</button>
        </div>
    );
};

interface CategoriesProps {
    categories?: ICategory[];
    updateProjects: () => void;
    updateCategories: () => void;
    clearSelectedProject: () => void;
}

function Categories({
    categories,
    updateProjects,
    updateCategories,
    clearSelectedProject,
}: CategoriesProps) {
    const dispatch = useAppDispatch();

    const [showHidden, setShowHidden] = useState(false);

    const currentProject = useAppSelector((state) => state.projects.currentProject);

    useEffect(() => {
        console.log("here");
        updateCategories();
    }, [currentProject.id, updateCategories]);

    const openAddCategory = () => {
        dispatch(
            create({
                id: 0,
                component: AddCategoryDialog,
                props: {
                    updateCategories: updateCategories,
                    projectId: currentProject.id,
                    orderId: (categories[0]?.orderId ?? 0) - 1,
                },
            }),
        );
    };

    const doProjectDelete = () => {
        dispatch(
            create({
                id: 0,
                component: ConfirmDialog,
                props: {
                    resultListener: (result: boolean) => {
                        if (result)
                            deleteProject(currentProject.id).then(() => {
                                clearSelectedProject();
                                updateProjects();
                            });
                    },
                    warningText: "This action can not be undone.",
                },
            }),
        );
    };

    const downloadCsv = () => {
        dispatch(
            create({
                id: 0,
                component: DownloadCsvDialog,
                props: {
                    project: currentProject,
                    categories: categories,
                },
            }),
        );
    };

    const getHours = (chargable: boolean): number => {
        if (!categories) return -1;

        let sum: number = categories
            .reduce((prev, current) => prev.concat(current.periods), [])
            .filter((p: IPeriod) => p.chargable === chargable)
            .reduce((prev, current) => {
                if (!current.endDateTime || !current.startDateTime) return prev;

                let duration = current.endDateTime.toMillis() - current.startDateTime.toMillis();
                duration = Math.round((duration / 1000 / 60 / 60) * 100) / 100;

                return prev + duration;
            }, 0);

        return sum;
    };

    const moveCategory = async (
        direction: "FIRST" | "LEFT" | "RIGHT" | "LAST",
        category: ICategory,
    ) => {
        let currentIndex = categories.findIndex((c) => c.id === category.id);
        let target: number;
        switch (direction) {
            case "FIRST":
                target = (categories[0].orderId ?? 0) - 1;
                break;
            case "LEFT":
                if (currentIndex === 0) return;
                const leftNeighbour = categories[currentIndex - 1];
                target = leftNeighbour.orderId ?? 0;
                leftNeighbour.orderId = target + 1;
                await updateCategory(currentProject.id, leftNeighbour);
                break;
            case "RIGHT":
                if (currentIndex === categories.length - 1) return;
                const rightNeighbour = categories[currentIndex + 1];
                target = rightNeighbour.orderId ?? 1;
                rightNeighbour.orderId = target - 1;
                await updateCategory(currentProject.id, rightNeighbour);
                break;
            case "LAST":
                target = (categories[categories.length - 1].orderId ?? 0) + 1;
                break;
        }

        category.orderId = target;
        await updateCategory(currentProject.id, category);
        updateCategories();
    };

    return (
        <div className="categories-container">
            <div className="top-row">
                <h1>
                    {currentProject.title} ({showHidden ? "All" : "Not Hidden"})
                </h1>

                <button
                    onClick={() => {
                        setShowHidden(!showHidden);
                    }}
                >
                    {showHidden ? "Don't show" : "Show"} Hidden
                </button>

                <button onClick={downloadCsv}>Export csv</button>

                <button className="add-category" onClick={openAddCategory}>
                    Add Category
                </button>

                <button className="warn" onClick={doProjectDelete}>
                    Delete
                </button>
            </div>

            <div>
                {getHours(true)} hours chargable, {getHours(false)} hours not chargable
            </div>

            <div className="columns">
                {categories
                    ?.filter((c) => showHidden || !c.hidden)
                    .map((c, i) => (
                        <CategoryColumn
                            key={i}
                            project={currentProject}
                            category={c}
                            updateCategories={updateCategories}
                            move={(direction) => moveCategory(direction, c)}
                        />
                    )) ?? <div>Loading...</div>}
            </div>
        </div>
    );
}

export default Categories;
